import videojs from 'video.js';
import { SwapStreamsButton } from '../../components/swap-streams-button.js';
import { getFullscreenToggleIndex } from '../../util';

const AUDIO_ONLY_STREAM_DIMENSIONS = {
    x: 0,
    y: 0,
    width: 0,
    height: 0
};

export class MultistreamPluginStub extends videojs.getPlugin('plugin') {
    constructor(player, options) {
        super(player, options);

        player.loader().onLoadPresentation(onLoadPresentation.bind(player));
    }
}

function sortByPlaybackAudioSource(a, b) {
    if (a.playbackAudioSource && !b.playbackAudioSource) {
        return -1;
    }
    if (b.playbackAudioSource && !a.playbackAudioSource) {
        return 1;
    }
    return 0;
}

function shouldLoad(presentationId, playCoverData, presentationData) {
    return (presentationData?.streams?.length ?? 0) > 1;
}

function findVisualItemBySourceId(composition, sourceId) {
    return composition.VisualItems.find(item => item.SourceId === sourceId);
}

function organizeStreams(composition, streams) {
    let organizedStreams = [];
    for (let i = 0; i < streams.length; i++) {
        let stream = streams[i];
        if (stream.audioOnly === true) {
            //AudioOnly streams are hidden, but kept in player for audio
            organizedStreams.push(Object.assign({}, stream, AUDIO_ONLY_STREAM_DIMENSIONS));
        } else if (!findVisualItemBySourceId(composition, stream.id)) {
            //Streams not in composition are not included
            continue;
        } else {
            //Streams in composition are assigned dimensions
            const visualItem = findVisualItemBySourceId(composition, stream.id);
            const streamLayoutInfo = {
                x: visualItem.TargetRect.X,
                y: visualItem.TargetRect.Y,
                width: visualItem.TargetRect.Width,
                height: visualItem.TargetRect.Height
            };
            organizedStreams.push(Object.assign({}, stream, streamLayoutInfo));
        }
    }
    return organizedStreams.sort(sortByPlaybackAudioSource);
}

// Parses the information from the composition model to set up the background styling of the multi-stream player
function setupBackground(player, comp) {
    const backgroundComposition = comp.VisualItems.find(item => item.ZIndex === 0);
    if (backgroundComposition) {
        player.multistream().setBackgroundImage(backgroundComposition.SourceId);
    }

    //rather than else if, we'll pull the color into the player even if there is a background image
    //although it isn't seen right now if there is, due to the background image fully covering the space
    if (comp.BaseColor) {
        player.multistream().setBackgroundColor(comp.BaseColor);
    }    
}

async function onLoadPresentation(presentationId, playCoverData, presentationData) {
    const player = this;
    if (shouldLoad(...arguments)) {
        if (!videojs.getPlugin('multistream')) {
            const { MultistreamPlugin } = await import(/* webpackChunkName: 'multistream' */ './multistream.js');
            player.registerPlugin('multistream', MultistreamPlugin);
        }
        
        player.multistream().init();

        const map = presentationData.compositionMap;
        const comp = map?.VisualCompositions[0];
        setupBackground(player, comp);

        let streams = presentationData.streams;

        //Filter streams absent from composition, hide audioonly streams, assign dimensions included streams
        const organizedStreams = organizeStreams(comp, streams);
        //loader needs presentationData to have organized streams when adding src to player
        presentationData.streams = organizedStreams;
        // Wait for each stream to load
        const multistreams = organizedStreams.slice(1);
        const multistreamPlugin = player.multistream();
        return Promise.all(multistreams.map(stream => {
            return multistreamPlugin.add(stream);
        })).then(() => {
            // "Priority" property determines the order in which streams should appear on screen
            // PlaybackAudioSource determines which stream is the "main" one attached to the top-level player.
            // Set the priority of the main stream in the multistream plugin layout
            const { x, y, width, height } = organizedStreams[0];
            multistreamPlugin.setMainStreamSize(x, y, width, height);
            
            const aspect = map.Width / map.Height;
            multistreamPlugin.aspect = aspect;

            // Initialize the stream swap button
            if (presentationData.playerOptions?.settings?.enableSwapStreams) {
                multistreamPlugin.compositionList = presentationData.compositionList;
                multistreamPlugin.primarySourceId = organizedStreams[0].id;

                const swapButton = new SwapStreamsButton(player);
                player.getChild('ControlBar').addChild(swapButton, null, getFullscreenToggleIndex(player) - 1);
            }

            return { organizedStreams };
        });
    }
}
